<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStreamMarkdown } from 'shared/composables/useStreamMarkdown';

const props = defineProps({
  content: { type: String, default: '' },
  isStreaming: { type: Boolean, default: false },
  render: { type: Function, required: true },
});

const emit = defineEmits(['contentClick']);

const { t } = useI18n();

const MS_IN_S = 1000;

const SHIMMER_LABEL =
  'inline-block bg-gradient-to-r from-n-slate-10 via-n-slate-12 to-n-slate-10 bg-[length:200%_auto] bg-clip-text text-transparent animate-text-shimmer';

const REASONING_PROSE =
  'text-xs leading-relaxed text-n-slate-10 prose prose-sm dark:prose-invert max-w-none [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:!my-2 [&_code]:text-[12px] [&_a.copilot-entity-link]:text-n-brand [&_a.copilot-entity-link]:font-medium';

const isOpen = ref(false);
const userPinnedOpen = ref(false);
const duration = ref(undefined);
const startTime = ref(null);

const showContent = computed(() => isOpen.value);

watch(
  () => props.isStreaming,
  (streaming, wasStreaming) => {
    if (streaming) {
      if (startTime.value === null) {
        startTime.value = Date.now();
      }
      if (!wasStreaming) {
        duration.value = undefined;
      }
      return;
    }

    if (startTime.value !== null) {
      duration.value = Math.ceil((Date.now() - startTime.value) / MS_IN_S);
      startTime.value = null;
    }

    if (wasStreaming && !userPinnedOpen.value) {
      isOpen.value = false;
    }
  },
  { immediate: true }
);

const triggerLabel = computed(() => {
  if (props.isStreaming) return null;
  if (duration.value === undefined || duration.value <= 0) {
    return t('COPILOT.REASONING_DURATION_FEW');
  }
  return t('COPILOT.REASONING_DURATION', { seconds: duration.value });
});

const contentSource = computed(() => props.content || '');

const { html: reasoningHtml } = useStreamMarkdown(contentSource, {
  render: text => props.render(text),
  isStreaming: () => props.isStreaming,
});

function toggleOpen() {
  isOpen.value = !isOpen.value;
  userPinnedOpen.value = isOpen.value;
}

function handleContentClick(event) {
  emit('contentClick', event);
}
</script>

<template>
  <div class="mb-2">
    <button
      type="button"
      class="relative flex w-full items-center gap-1.5 p-0 text-left text-xs text-n-slate-10 transition-colors duration-200 hover:text-n-slate-12"
      @click="toggleOpen"
    >
      <span
        class="i-lucide-brain size-3.5 absolute right-full mr-2 pointer-events-none"
      />
      <span v-if="isStreaming" :class="SHIMMER_LABEL">
        {{ t('COPILOT.THINKING') }}
      </span>
      <span
        v-else
        class="transition-opacity duration-300 ease-out"
        :class="showContent ? 'opacity-100' : 'opacity-90'"
      >
        {{ triggerLabel }}
      </span>
      <span
        class="i-lucide-chevron-right size-3.5 shrink-0 transition-transform duration-300 ease-out"
        :class="{ 'rotate-90': isOpen }"
      />
    </button>
    <div
      class="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
      :class="showContent ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <div class="overflow-hidden min-h-0">
        <div
          class="mt-1 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
          :class="[
            REASONING_PROSE,
            showContent
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-2 pointer-events-none',
          ]"
          @click="handleContentClick"
          v-html="reasoningHtml"
        />
      </div>
    </div>
  </div>
</template>
